import {VNode} from 'snabbdom';
import {ParamComponent} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {COHORT_METRIC_ENTRY_DEFAULT_PROPS} from 'common/report/util/cohorts/transformations/entries';
import {currentProjectStore} from 'common/report/stores/project-details';
import {useStore} from 'common/util/component-helpers';
import {emitTrackEvent} from 'common/util/dom';

import {QueryBuilderUpdateEvent} from '../query-builder/types';
import {CohortGroupEntry, RowTypeComplexOption, SelectorTypeDefinitions} from '../query-entry-section/types';
import {Cohort} from './models/cohort';
import {SerializedCohort} from './models/types/serialized-cohort-types';
import {StagedCohort} from './models/builder/staged-cohort';
import {SerializedStagedCohort} from './models/types/serialized-staged-cohort-types';
import {template} from './template';

interface CohortsQueryBuilderJsxProps {
  params: {
    cohort?: SerializedCohort;
    /** @todo [pablo] drop this to remove backwards compatibility with site calls using staged-cohort */
    // the serialized cohort you want this builder to render
    stagedCohort?: SerializedStagedCohort;
    projectId: number;
    projectHasCohorts?: boolean;
    dataGroupId?: string;
    supportB2bCohorts?: boolean;
    disabled?: boolean;
    trackName?: string;
    userId?: number;
    workspaceId?: number;
    projectAsOfDate?: string;
    maxDataHistoryDays?: string;
    projectHasPeople?: boolean;
    hideResetButton?: boolean;
    nonCollapsible: boolean;
    hideEditControls?: boolean;
    querySource?: string;
    headerTitle?: string;
    footerItems?: VNode;
  };
}

type CohortsQueryBuilderState = EmptyObj;

export class CohortsQueryBuilder extends ParamComponent<
  CohortsQueryBuilderJsxProps['params'],
  CohortsQueryBuilderState
> {
  get config() {
    return {
      params: {
        // the serialized cohort you want this builder to render
        cohort: {
          type: Object,
        },

        stagedCohort: {
          type: Object,
        },

        projectId: {
          type: Number,
        },

        projectHasCohorts: Boolean,
        headerTitle: String,
        hideResetButton: Boolean,
        hideEditControls: Boolean,

        // the data group id that this cohort-builder queries with
        dataGroupId: {
          type: String,
        },

        supportB2bCohorts: Boolean,

        // whether this builder can be edited
        disabled: {
          type: Boolean,
        },

        trackName: String,

        // ID of user viewing the builder
        userId: {
          type: Number,
        },

        workspaceId: {
          type: Number,
        },

        projectHasPeople: Boolean,

        // max days time menu can query into data history
        maxDataHistoryDays: {
          type: String,
        },

        querySource: String,
        nonCollapsible: Boolean,

        // as of date for projects that do not have real time ingestion
        projectAsOfDate: {
          type: String,
        },
        footerItems: Object,
      },

      defaultParams: {
        cohort: null,
        stagedCohort: null,
        dataGroupId: null,
        projectAsOfDate: ``,
      },

      template,
      defaultState: {},
      helpers: {},
    };
  }

  connectedCallback() {
    super.connectedCallback();

    useStore(this, currentProjectStore);
  }

  hasPeopleEnabled() {
    return currentProjectStore.value?.hasIntegratedEngage;
  }

  getProjectAsOfDate() {
    return currentProjectStore.value?.projectAsOfDate ?? ``;
  }

  get cohort() {
    const defaultCohort = Cohort.createCohortFromStaged(StagedCohort.deserialize(null)).serialize();
    return Cohort.parseCohort(this.params.cohort ?? defaultCohort);
  }

  get rowTypes(): RowTypeComplexOption[] {
    return [
      {
        value: SelectorTypeDefinitions.CohortGroup,
        placeholderEntry: {event: COHORT_METRIC_ENTRY_DEFAULT_PROPS.event},
      },
    ];
  }

  /**
   * Updates the selected cohort with the updated groups from the qb
   */
  handleQueryUpdate(ev: QueryBuilderUpdateEvent) {
    let cohort = this.cohort;
    cohort.groups = ev.detail.entries as CohortGroupEntry[];

    // prevent an empty cohort definition from being constructed
    if (cohort.groups.length === 0) {
      cohort = new Cohort({dataGroupId: this.params.dataGroupId});
    }

    const detail = {
      cohort,
      queryReason: ev.detail.queryReason,
    };

    this.dispatchEvent(new CustomEvent(`cohortUpdated`, {detail}));
    this.trackUpdate(cohort);
  }

  trackUpdate(cohort: Cohort) {
    emitTrackEvent(this, {
      eventName: `[Cohorts] Update cohort`,
      props: {
        ...cohort.toTrackingData(),
        // Track Query Source for sessions
        ...(this.params.querySource
          ? {
              'Query Source': this.params.querySource,
            }
          : {}),
      },
    });
  }
}

export const CohortsBuilderEl = defineAndMakeJsxFactory<CohortsQueryBuilderJsxProps>(
  `mp-cohorts-query-builder`,
  CohortsQueryBuilder,
);
export const CohortsBuilderReact = panel2react<CohortsQueryBuilderJsxProps>(
  `mp-cohorts-query-builder`,
  CohortsQueryBuilder,
);
